// src/components/AccountChecker/AccountResults.jsx
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export function isHashValue(val) {
  if (!val) return false;
  const str = String(val).trim();
  if (/^\$2[aby]?\$\d{2}\$/.test(str)) return true; // BCrypt
  if (/^\$(argon2id|argon2i|argon2d)\$/.test(str)) return true; // Argon2
  if (/^\$(1|5|6)\$/.test(str)) return true; // Unix crypt
  if (/^[a-fA-F0-9]{32}$/.test(str)) return true; // MD5
  if (/^[a-fA-F0-9]{40}$/.test(str)) return true; // SHA-1
  if (/^[a-fA-F0-9]{64}$/.test(str)) return true; // SHA-256
  if (/^[a-fA-F0-9]{128}$/.test(str)) return true; // SHA-512
  return false;
}

const AccountResults = ({
  results = [],
  totalCount = 0,
  isLoading = false,
  searchQuery = '',
  activeTargetUser = '',
  hasSearched = false,
  onShowToast = () => { },
  onShowAll = null,
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const [copiedKey, setCopiedKey] = useState(null);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Check if any results contain a hash
  const hasAnyHash = useMemo(() => {
    return results.some(item => item.isHash || isHashValue(item.pass || item.password));
  }, [results]);

  const copyToClipboard = useCallback((text, key, isHash) => {
    const onCopied = () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      setCopiedKey(key);
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedKey(null);
        copyTimeoutRef.current = null;
      }, 1500);
      onShowToast(
        isHash
          ? (isEn ? 'Hash copied to clipboard' : 'Хэш скопирован в буфер обмена')
          : (isEn ? 'Password copied to clipboard' : 'Пароль скопирован в буфер обмена')
      );
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onCopied).catch(() => {
        onCopied();
      });
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        onCopied();
      } catch {
        onShowToast(isEn ? 'Failed to copy to clipboard' : 'Не удалось скопировать в буфер');
      }
    }
  }, [isEn, onShowToast]);

  const handleCopyAll = useCallback(() => {
    if (!results.length) {
      onShowToast(isEn ? 'No results to copy' : 'Нет данных для копирования');
      return;
    }
    let buffer = `STORM CHECK EXPORT — ${searchQuery}\n`;
    results.forEach(acc => {
      const name = acc.name || acc.username || searchQuery;
      const pass = acc.pass || acc.password || '';
      const source = acc.source || 'Database Leak';
      const date = acc.date || acc.lastVerified || '2026';
      const isHash = acc.isHash || isHashValue(pass);
      const credType = isHash ? 'ХЕШ' : 'ПАРОЛЬ';
      buffer += `${name}:${pass} | ${credType} | ${source} (${date})\n`;
    });

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(buffer).then(() => {
        onShowToast(
          isEn
            ? `Copied ${results.length} credentials to clipboard`
            : `Скопировано ${results.length} учетных записей`
        );
      });
    }
  }, [results, searchQuery, isEn, onShowToast]);

  const handleExport = useCallback((type) => {
    if (!results.length) {
      onShowToast(isEn ? 'No results to export' : 'Нет результатов для экспорта');
      return;
    }

    if (type === 'json') {
      const exportArray = results.map(acc => {
        const name = acc.name || acc.username || searchQuery;
        const pass = acc.pass || acc.password || '';
        const source = acc.source || 'Database Leak';
        const date = acc.date || acc.lastVerified || '2026';
        const isHash = acc.isHash || isHashValue(pass);
        return {
          username: name,
          credential: pass,
          type: isHash ? 'HASH' : 'PASSWORD',
          breachSource: source,
          breachDate: date,
        };
      });
      const blob = new Blob([JSON.stringify(exportArray, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storm_check_${searchQuery || 'export'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onShowToast(isEn ? 'JSON file exported' : 'Файл JSON экспортирован');
    } else {
      let txt = `STORM CHECK EXPORT [${new Date().toISOString()}]\nTARGET USER: ${searchQuery}\n\n`;
      results.forEach(acc => {
        const name = acc.name || acc.username || searchQuery;
        const pass = acc.pass || acc.password || '';
        const source = acc.source || 'Database Leak';
        const date = acc.date || acc.lastVerified || '2026';
        const isHash = acc.isHash || isHashValue(pass);
        const credType = isHash ? 'HASH' : 'PASSWORD';
        txt += `${name}:${pass} [${credType}] (${source}, ${date})\n`;
      });
      const blob = new Blob([txt], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storm_check_${searchQuery || 'export'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onShowToast(isEn ? 'TXT file exported' : 'Текстовый файл TXT экспортирован');
    }
  }, [results, searchQuery, isEn, onShowToast]);

  return (
    <section className="glass-card-module results-panel-box">
      {/* Верхняя плашка результатов */}
      <div className="results-head-bar">
        <div className="results-title-group">
          <h3 className="results-main-title">
            {isEn ? 'Account Search Results' : 'Результаты Поиска Аккаунтов'}
          </h3>
          {hasSearched && (totalCount > 0 || results.length > 0) && !isLoading && (
            <span className="results-count-pill">
              {totalCount > results.length
                ? (isEn ? `Showing ${results.length} of ${totalCount}` : `Показано ${results.length} из ${totalCount}`)
                : `${results.length} ${isEn ? 'results' : (results.length % 10 === 1 && results.length !== 11 ? 'находка' : (results.length % 10 >= 2 && results.length % 10 <= 4 ? 'находки' : 'находок'))}`
              }
            </span>
          )}
        </div>

        <div className="results-action-buttons">
          <button
            type="button"
            className="res-tool-btn"
            onClick={() => handleExport('json')}
            disabled={!results.length || isLoading}
            title={isEn ? 'Export results as JSON' : 'Экспортировать результаты в формате JSON'}
          >
            <svg viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>JSON</span>
          </button>
          <button
            type="button"
            className="res-tool-btn"
            onClick={() => handleExport('txt')}
            disabled={!results.length || isLoading}
            title={isEn ? 'Export results as TXT' : 'Экспортировать результаты в текстовый файл'}
          >
            <svg viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span>TXT</span>
          </button>
          <button
            type="button"
            className="res-tool-btn"
            onClick={handleCopyAll}
            disabled={!results.length || isLoading}
            title={isEn ? 'Copy all credentials to clipboard' : 'Скопировать все учетные данные в буфер'}
          >
            <svg viewBox="0 0 24 24">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            <span className="res-tool-text-full">{isEn ? 'Copy all' : 'Скопировать все'}</span>
            <span className="res-tool-text-short">{isEn ? 'Copy' : 'Копировать'}</span>
          </button>
        </div>
      </div>

      {/* Баннер hashes.com при наличии хэшей */}
      {hasAnyHash && !isLoading && (
        <div className="hashes-notice-banner">
          <div className="hashes-notice-left">
            <div className="hashes-notice-icon">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div className="hashes-notice-content">
              <div className="hashes-notice-title">
                {isEn ? 'Password hash detected in results' : 'В результатах обнаружен хэш пароля'}
              </div>
              <div className="hashes-notice-sub">
                {isEn ? (
                  <>You can decrypt detected hashes via the online tool <strong>hashes.com</strong></>
                ) : (
                  <>Вы можете расшифровать найденные хэши через онлайн-сервис <strong>hashes.com</strong></>
                )}
              </div>
            </div>
          </div>
          <a
            href="https://hashes.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hashes-open-btn"
            title={isEn ? 'Open hashes.com' : 'Перейти на hashes.com'}
          >
            <span>hashes.com</span>
            <svg viewBox="0 0 24 24">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        </div>
      )}

      {/* Состояние скелетона при поиске */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="skeleton-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(255,255,255,0.1)' }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div className="skeleton-line" style={{ width: '35%' }} />
                  <div className="skeleton-line" style={{ width: '55%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Контейнер карточек найденных записей */}
      {!isLoading && results.length > 0 && (
        <div className="accounts-list-container">
          {results.map((acc, index) => {
            const nick = acc.name || acc.username || searchQuery;
            const pass = acc.pass || acc.password || '';
            const source = acc.source || 'Leak Database';
            const date = acc.date || acc.lastVerified || '2026-08-18';
            const isHash = acc.isHash || isHashValue(pass);
            const key = `${nick}_${index}_${pass}`;
            const isCopied = copiedKey === key;

            return (
              <div key={key} className="account-row-card">
                <div className="account-info-left">
                  <div className="player-head-frame">
                    <img
                      src={`https://mc-heads.net/avatar/${encodeURIComponent(nick)}/44`}
                      alt={nick}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'44\' height=\'44\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\'><rect width=\'18\' height=\'18\' x=\'3\' y=\'3\' rx=\'2\'/></svg>';
                      }}
                    />
                  </div>
                  <div className="player-meta-box">
                    <div className="player-title-row">
                      <span className="player-name">{nick}</span>
                      <span className="leak-source-badge">{source}</span>
                      <span className="leak-date-text">{date}</span>
                    </div>
                  </div>
                </div>

                <div className="account-actions-right">
                  <button
                    type="button"
                    className={`cred-copy-pill ${isCopied ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(pass, key, isHash)}
                    title={isEn ? `Click to copy ${isHash ? 'hash' : 'password'}` : `Нажмите, чтобы скопировать ${isHash ? 'хэш' : 'пароль'}`}
                  >
                    <span className="cred-type-badge">{isHash ? (isEn ? 'HASH' : 'ХЕШ') : (isEn ? 'PASSWORD' : 'ПАРОЛЬ')}</span>
                    <span className="cred-val-text">{pass}</span>
                    <svg className="cred-copy-icon" viewBox="0 0 24 24">
                      {isCopied ? (
                        <polyline points="20 6 9 17 4 12" />
                      ) : (
                        <>
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}

          {/* Уведомление об ограничении лимитом вывода и кнопка "Показать все" */}
          {totalCount > results.length && (
            <div style={{
              marginTop: '16px',
              padding: '12px 18px',
              borderRadius: '14px',
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <span style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)' }}>
                {isEn
                  ? `Showing ${results.length} out of ${totalCount} records (limited by search settings).`
                  : `Показано ${results.length} из ${totalCount} найденных записей (ограничено настройками поиска).`}
              </span>
              {onShowAll && (
                <button
                  type="button"
                  onClick={onShowAll}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '8px',
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.35)',
                    color: '#4ade80',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                  className="hover:bg-emerald-500/25 active:scale-95"
                >
                  {isEn ? `Show all ${totalCount}` : `Показать все ${totalCount}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Начальное состояние: поиск ещё не выполнялся */}
      {!isLoading && !hasSearched && (
        <div className="empty-results-state">
          <div className="empty-state-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="empty-title">
            {isEn ? 'No Search Conducted' : 'Поиск ещё не выполнялся'}
          </div>
          <p className="empty-desc">
            {isEn
              ? 'Enter a Minecraft username in the search field above to check for leaked credentials from the backend database.'
              : 'Введите имя пользователя Minecraft в поле поиска выше, чтобы запросить проверку учётных данных из базы данных сервера.'}
          </p>
        </div>
      )}

      {/* Пустое состояние: поиск выполнен, но ничего не найдено */}
      {!isLoading && hasSearched && results.length === 0 && (
        <div className="empty-results-state">
          <div className="empty-state-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="1.8" />
              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="empty-title">
            {isEn
              ? `No records found for "${activeTargetUser || searchQuery}"`
              : `Записей не обнаружено для "${activeTargetUser || searchQuery}"`}
          </div>
          <p className="empty-desc">
            {isEn
              ? 'The user was not found in the selected leak database or credentials were salted and protected without direct matches.'
              : 'Пользователь не найден в выбранной базе утечек или учетные данные были надежно защищены соленым BCrypt хэшем без прямых совпадений.'}
          </p>
        </div>
      )}
    </section>
  );
};

export default AccountResults;